import { useTranslation } from "react-i18next";
import FormField from "../../../components/FormField";
import { Device, SimSettings } from "../../../models/device";
import { useEffect, useState } from "react";
import { useCliftContext } from "../../../hooks/useCliftContext";
import { CliftReducerAction } from "../../../context/clift-context/clift-action-types";
import { ConfirmDialog } from "../../../components/ConfirmDialog";
import {
  getErrorCode,
  getLocalizedErrorReason,
} from "../../../services/clift-api-errors";
import { updateDevice } from "../../../services/devices-api";
import SideModal from "../../../components/SideModal";
import "./SimSettingsForm.css";
import { toast } from "react-toast";

const DEFAULT_SIM_SETTINGS = {
  apn: "",
  username: "",
  password: "",
};

interface SimSettingsFormProps {
  tenantID: number;
  device: Device;
}

export const SimSettingsForm = ({ tenantID, device }: SimSettingsFormProps) => {
  const { t } = useTranslation();
  const { dispatchCliftState } = useCliftContext();
  const [settings, setSettings] = useState<SimSettings>(DEFAULT_SIM_SETTINGS);
  const [hasChanged, setChanged] = useState(false);
  const [cancelled, setCancelled] = useState(false);

  const closeSidePanel = () => {
    dispatchCliftState({
      type: CliftReducerAction.CloseSidePanel,
    });
  };

  const onValueChanged = (name: string, value: string | number) => {
    setChanged(true);
    setSettings({
      ...settings,
      [name]: value as string,
    });
  };

  const onClose = () => {
    if (hasChanged) {
      setCancelled(true);
    } else {
      closeSidePanel();
    }
  };

  const onSubmit = (simSettings: SimSettings) => {
    if (!simSettings) onClose();

    const updatedDevice = {
      ...device,
      simSettings,
    };

    return updateDevice(tenantID, updatedDevice)
      .then(() => {
        closeSidePanel();
        dispatchCliftState({
          type: CliftReducerAction.AddAlert,
          alert: t("device_sim_settings_update_success", {
            ns: "alerts",
            deviceId: updatedDevice.id,
          }),
        });
        toast.success(
          t("changes_saved", {
            ns: "toasts",
          })
        );
      })
      .catch((err) => {
        closeSidePanel();
        dispatchCliftState({
          type: CliftReducerAction.AddAlert,
          alert: t("device_sim_settings_update_failed", {
            ns: "alerts",
            deviceId: updatedDevice.id,
            code: getErrorCode(err),
          }),
        });
        toast.error(
          t("error") +
            " " +
            t("edit_failed", {
              ns: "toasts",
              reason: getLocalizedErrorReason(err),
            })
        );
      });
  };

  useEffect(() => {
    setSettings(device.simSettings ? device.simSettings : DEFAULT_SIM_SETTINGS);
  }, [device.simSettings]);

  const onSave = () => {
    onSubmit(settings);
  };

  const bottomButtonsProps = {
    onCancel: onClose,
    showSave: true,
    onSave: onSave,
  };

  return (
    <SideModal
      title={t("sim_edit_settings", { ns: "lift" })}
      bottomButtonsProps={bottomButtonsProps}
      onClose={onClose}
    >
      <div className="sim-settings-form">
        <form className="sim-settings-form">
          <div className="sim-settings-form-lines">
            <FormField
              name={"apn"}
              title={t("sim_apn", { ns: "lift" })}
              value={settings.apn ?? ""}
              autoComplete="off"
              onValueChanged={onValueChanged}
            />
            <FormField
              name={"username"}
              title={t("sim_username", { ns: "lift" })}
              value={settings.username ?? ""}
              autoComplete="off"
              onValueChanged={onValueChanged}
            />
            <FormField
              name={"password"}
              title={t("sim_password", { ns: "lift" })}
              value={settings.password ?? ""}
              onValueChanged={onValueChanged}
              autoComplete="off"
              type="password"
            />
          </div>
        </form>

        <ConfirmDialog
          isOpen={cancelled}
          onCancel={() => {
            setCancelled(false);
          }}
          onConfirm={() => {
            closeSidePanel();
          }}
        />
      </div>
    </SideModal>
  );
};
